import { LoroDoc, LoroMap, LoroMovableList } from "loro-crdt";
import {
  stateShapeSchema,
  isDerivedShapeInput,
  MapShapeInput,
  tentativeShapeSchema,
} from "./shape";
import { idLib } from "./id";
import { positionLib } from "./position";
import { Corner, cornerEnum } from "./rectangle";
import { z } from "zod";
import { MAP_TYPES } from "@larki/shared/types/user.type";

const userSettingsSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  image: z.string().nullish(),
  color_index: z.number().nonnegative(),
  map_type: z.enum(MAP_TYPES).default("roadmap"),
});
export type UserSettings = z.infer<typeof userSettingsSchema>;

export const customOrderSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.tuple([z.number(), z.number(), z.number()]),
  price: z
    .union([z.number(), z.literal("TBC")])
    .optional()
    .default("TBC"),
});
export type CustomOrder = z.infer<typeof customOrderSchema>;

export const stateSchema = z.object({
  shapes: z.array(stateShapeSchema).optional(),
  custom_orders: z.array(customOrderSchema).optional(),
});
export const tempStateSchema = z.object({
  hidden_map: z.record(z.string(), z.boolean()).optional(),
  tentative_shapes: z.record(z.string(), tentativeShapeSchema).optional(),
  clients: z
    .record(
      z.string(),
      z.object({
        drag_shape_index: z.number().optional(),
        drag_hole_index: z.number().optional(),
        drag_vertex_index: z.number().optional(),
        drag_corner: cornerEnum.optional(),
        user_settings: userSettingsSchema,
      }),
    )
    .optional(),
});

const createShape = (state: LoroDoc, input: MapShapeInput, name: string) => {
  const newShapeId = idLib.shortId();

  // transaction
  {
    const shapesList = state.getMovableList("shapes");
    const shapeContainer = shapesList.pushContainer(new LoroMap());

    shapeContainer.set("type", input.type);
    shapeContainer.set("id", newShapeId);
    shapeContainer.set("name", name);
    shapeContainer.set("dataType", input.dataType);

    if (isDerivedShapeInput(input)) {
      shapeContainer.set("baseShapeId", input.baseShapeId);
    }

    if (input.type === "polygon") {
      const exteriorContainer = shapeContainer.setContainer(
        "exterior",
        new LoroMovableList(),
      );
      for (const [i, vertex] of input.exterior.entries()) {
        exteriorContainer.insert(i, positionLib.toString(vertex));
      }
    } else if (input.type === "rectangle") {
      const exterior = shapeContainer.setContainer("exterior", new LoroMap());
      exterior.set(Corner.sw, positionLib.toString(input.exterior.sw));
      exterior.set(Corner.se, positionLib.toString(input.exterior.se));
      exterior.set(Corner.ne, positionLib.toString(input.exterior.ne));
      exterior.set(Corner.nw, positionLib.toString(input.exterior.nw));
    } else if (input.type === "path") {
      const verticesContainer = shapeContainer.setContainer(
        "vertices",
        new LoroMovableList(),
      );
      for (const [i, vertex] of input.vertices.entries()) {
        verticesContainer.insert(i, positionLib.toString(vertex));
      }
    }

    if (
      (input.type === "polygon" || input.type === "rectangle") &&
      input.holes
    ) {
      const holesContainer = shapeContainer.setContainer(
        "holes",
        new LoroMovableList(),
      );
      for (const [i, hole] of input.holes.entries()) {
        const holeList = holesContainer.insertContainer(
          i,
          new LoroMovableList(),
        );
        for (const [j, vertex] of hole.entries()) {
          holeList.insert(j, positionLib.toString(vertex));
        }
      }
    }
  }

  return newShapeId;
};

export const stateBuilder = {
  createShape,
};
